import { useMemo, useState } from 'react';
import { Empty, ErrorBanner, Loading } from '../../components/ui';
import {
  OPEN_REC_STATUSES,
  REC_PRIORITIES,
  REC_STATUSES,
  annualisedValue,
  bucketTotals,
  costLinesOf,
  priorityRank,
  topPriority,
} from '../../lib/amCosts';
import { downloadCsv, fileStamp } from '../../lib/download';
import { formatNumber } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { AmPipelineRec } from '../../lib/types';
import { BucketTiles, CostCell, PriorityBadge, StateBadge } from './amUi';

const UNASSIGNED = '__none__';
const OPEN = '__open__';

/**
 * Every recommendation across the book of business.
 *
 * The client page answers "what does this client need"; this answers "what is
 * this business carrying, and who is carrying it". Those are different
 * questions, and the second one is the one that gets forgotten — a recommendation
 * raised in an audit and never followed up is invisible from the client page
 * because nobody opens a client page they are not already thinking about.
 *
 * It defaults to open work only. Completed and declined items are settled; a
 * pipeline that includes them is a history, not a pipeline.
 */
export function AmPipeline({ onOpenClient }: { onOpenClient: (clientId: string) => void }) {
  const resource = useResource<AmPipelineRec[]>('/am/recommendations');
  const [manager, setManager] = useState('');
  const [client, setClient] = useState('');
  const [status, setStatus] = useState(OPEN);
  const [priority, setPriority] = useState('');
  const [search, setSearch] = useState('');
  const [groupByClient, setGroupByClient] = useState(true);

  const recs = resource.data ?? [];

  const managers = useMemo(
    () => [...new Set(recs.map((r) => (r.AccountManager ?? '').trim()).filter(Boolean))].sort(),
    [recs],
  );
  const clients = useMemo(
    () => [...new Set(recs.map((r) => r.ClientName ?? '').filter(Boolean))].sort(),
    [recs],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return recs.filter((r) => {
      const owner = (r.AccountManager ?? '').trim();
      if (manager === UNASSIGNED ? owner !== '' : manager && owner !== manager) return false;
      if (client && r.ClientName !== client) return false;
      if (
        status === OPEN
          ? !(OPEN_REC_STATUSES as readonly string[]).includes(r.Status ?? '')
          : status && r.Status !== status
      ) {
        return false;
      }
      if (priority && topPriority(r) !== priority) return false;
      if (q && !`${r.Title ?? ''} ${r.ClientName ?? ''} ${r.Finding ?? ''}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [recs, manager, client, status, priority, search]);

  const hours = filtered.reduce((sum, r) => sum + (r.Hours ?? 0), 0);

  /* Roll-up per account manager, so it is obvious who is carrying what. Sorted
     by annualised value rather than count: ten small items is not the same
     workload as one large one. */
  const byManager = useMemo(() => {
    const map = new Map<
      string,
      { name: string; recs: number; clients: Set<string>; hours: number; buckets: ReturnType<typeof bucketTotals> }
    >();
    for (const rec of filtered) {
      const key = (rec.AccountManager ?? '').trim() || 'Unassigned';
      const entry =
        map.get(key) ??
        { name: key, recs: 0, clients: new Set<string>(), hours: 0, buckets: { oneTime: 0, monthly: 0, annual: 0 } };
      const own = bucketTotals([rec]);
      entry.recs += 1;
      entry.clients.add(rec.ClientName ?? 'Unassigned');
      entry.hours += rec.Hours ?? 0;
      entry.buckets = {
        oneTime: entry.buckets.oneTime + own.oneTime,
        monthly: entry.buckets.monthly + own.monthly,
        annual: entry.buckets.annual + own.annual,
      };
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => annualisedValue(b.buckets) - annualisedValue(a.buckets));
  }, [filtered]);

  const rows = useMemo(
    () =>
      [...filtered].sort(
        (a, b) =>
          (groupByClient ? String(a.ClientName).localeCompare(String(b.ClientName)) : 0) ||
          priorityRank(topPriority(b)) - priorityRank(topPriority(a)),
      ),
    [filtered, groupByClient],
  );

  function exportCsv() {
    const data: unknown[][] = [
      [
        'Client',
        'Account manager',
        'Client status',
        'Recommendation',
        'Finding',
        'Audit',
        'Priority',
        'Cost',
        'Cost type',
        'Hours',
        'Effort',
        'Status',
      ],
    ];
    for (const rec of rows) {
      /* One row per cost LINE, not per recommendation. A two-line
         recommendation collapsed into one row would have to pick a cost type
         and lie about the other. */
      for (const line of costLinesOf(rec)) {
        data.push([
          rec.ClientName ?? '',
          rec.AccountManager ?? 'Unassigned',
          rec.ClientStatus ?? '',
          rec.Title ?? '',
          rec.Finding ?? '',
          rec.AuditName ?? '',
          line.priority,
          line.cost,
          line.costType,
          rec.Hours ?? 0,
          rec.Effort ?? '',
          rec.Status ?? '',
        ]);
      }
    }
    downloadCsv(`recommendation-pipeline-${fileStamp()}.csv`, data);
  }

  if (resource.loading) return <Loading label="Loading the pipeline" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;

  const clearFilters = () => {
    setManager('');
    setClient('');
    setStatus('');
    setPriority('');
    setSearch('');
  };

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="small muted">
          {filtered.length} of {recs.length} recommendations across{' '}
          {new Set(filtered.map((r) => r.ClientName)).size} client
          {new Set(filtered.map((r) => r.ClientName)).size === 1 ? '' : 's'}
        </p>
        <div className="row wrap">
          <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>
            Clear filters
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={rows.length === 0}
            onClick={exportCsv}
          >
            Export CSV
          </button>
        </div>
      </div>

      <BucketTiles recs={filtered} hours={hours} />

      <div className="card mb-4">
        <div className="card-body">
          <div className="row wrap">
            <input
              className="input"
              style={{ maxWidth: 240 }}
              placeholder="Search recommendations…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search recommendations"
            />
            <select
              className="select"
              style={{ width: 'auto' }}
              value={manager}
              onChange={(e) => setManager(e.target.value)}
              aria-label="Account manager"
            >
              <option value="">All account managers</option>
              {managers.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              <option value={UNASSIGNED}>Unassigned</option>
            </select>
            <select
              className="select"
              style={{ width: 'auto' }}
              value={client}
              onChange={(e) => setClient(e.target.value)}
              aria-label="Client"
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              className="select"
              style={{ width: 'auto' }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Status"
            >
              <option value={OPEN}>Open — proposed, approved, in progress</option>
              <option value="">Any status</option>
              {REC_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              className="select"
              style={{ width: 'auto' }}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              aria-label="Priority"
            >
              <option value="">All priorities</option>
              {REC_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={groupByClient}
                onChange={(e) => setGroupByClient(e.target.checked)}
              />
              Group by client
            </label>
          </div>
        </div>
      </div>

      {byManager.length > 0 && (
        <section className="card mb-4">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">Who is carrying it</span>
              <h2>By account manager</h2>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Account manager</th>
                  <th className="num" style={{ width: 80 }}>Clients</th>
                  <th className="num" style={{ width: 70 }}>Recs</th>
                  <th className="num" style={{ width: 110 }}>One-time</th>
                  <th className="num" style={{ width: 110 }}>Monthly</th>
                  <th className="num" style={{ width: 110 }}>Annual</th>
                  <th className="num" style={{ width: 90 }}>Hours</th>
                </tr>
              </thead>
              <tbody>
                {byManager.map((entry) => (
                  <tr key={entry.name}>
                    <td className="strong">{entry.name}</td>
                    <td className="num">{entry.clients.size}</td>
                    <td className="num">{entry.recs}</td>
                    <td className="num">{formatNumber(entry.buckets.oneTime, 'currency')}</td>
                    <td className="num">{formatNumber(entry.buckets.monthly, 'currency')}</td>
                    <td className="num">{formatNumber(entry.buckets.annual, 'currency')}</td>
                    <td className="num">{entry.hours.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card">
        <div className="card-head">
          <div className="grow">
            <span className="eyebrow">Every item</span>
            <h2>Recommendations</h2>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="card-body">
            <Empty title="Nothing matches">
              <p>No recommendation matches these filters.</p>
            </Empty>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 200 }}>Client</th>
                  <th>Recommendation</th>
                  <th style={{ width: 100 }}>Priority</th>
                  <th className="num" style={{ width: 130 }}>Price</th>
                  <th className="num" style={{ width: 70 }}>Hours</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((rec, i) => {
                  /* Only label the client on its first row when grouping — a
                     repeated name down forty rows is noise. */
                  const newClient =
                    !groupByClient || i === 0 || rows[i - 1]?.ClientName !== rec.ClientName;
                  return (
                    <tr key={rec.AmRecommendationId}>
                      <td>
                        {newClient && (
                          <>
                            <div className="strong">{rec.ClientName}</div>
                            <div className="xs muted">{rec.AccountManager ?? 'Unassigned'}</div>
                          </>
                        )}
                      </td>
                      <td>
                        <div>{rec.Title}</div>
                        {rec.AuditName && <div className="xs muted">From {rec.AuditName}</div>}
                        {rec.Effort && <div className="xs muted">Effort: {rec.Effort}</div>}
                      </td>
                      <td>
                        <PriorityBadge priority={topPriority(rec)} />
                      </td>
                      <td className="num">
                        <CostCell rec={rec} />
                      </td>
                      <td className="num">{rec.Hours ? rec.Hours.toFixed(1) : '—'}</td>
                      <td>
                        <StateBadge status={rec.Status} />
                      </td>
                      <td className="actions">
                        {rec.ResolvedClientId && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => onOpenClient(rec.ResolvedClientId as string)}
                          >
                            Open client
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
